/**
 * WebAuthn registration and attestation ceremony helpers.
 *
 * - registerAttester(): navigator.credentials.create with ES256, registers
 *   the credential server-side. Returns the stored AttesterIdentity.
 *
 * - attestLedgerHead(): navigator.credentials.get with challenge =
 *   SHA-256(canonicalJSON({vehicleId, headHash, statement, createdAt})),
 *   binding who/what/words/when in a single Touch ID tap.
 *
 * Capacitor-readiness: navigator.credentials is the abstraction boundary.
 * Post-migration, only this file changes; components and verify.ts are
 * unaffected.
 *
 * Online-only: all functions here require network (POST to gg-app). The
 * ProvenancePanel shows an explicit offline state; no silent degradation.
 */

import { apiFetch } from '@/lib/api';
import type { AttesterIdentity } from '@/types/domain';
import { computeAttestationChallenge, decodeBase64url, encodeBase64url } from './crypto';

// ---------------------------------------------------------------------------
// Error types
// ---------------------------------------------------------------------------

export class WebAuthnError extends Error {
  constructor(
    public readonly code:
      | 'CANCELLED'
      | 'NO_AUTHENTICATOR'
      | 'NOT_ALLOWED'
      | 'INVALID_STATE'
      | 'UNSUPPORTED'
      | 'NETWORK'
      | 'UNKNOWN',
    message: string,
    causeValue?: unknown,
  ) {
    super(message, { cause: causeValue });
    this.name = 'WebAuthnError';
  }
}

/** Map a DOMException from navigator.credentials to a WebAuthnError. */
function mapCredentialError(err: unknown): WebAuthnError {
  if (err instanceof DOMException) {
    switch (err.name) {
      case 'NotAllowedError':
        return new WebAuthnError('CANCELLED', 'User cancelled or timed out', err);
      case 'InvalidStateError':
        return new WebAuthnError(
          'INVALID_STATE',
          'Credential already registered on this device',
          err,
        );
      case 'NotSupportedError':
        return new WebAuthnError('UNSUPPORTED', 'This device does not support WebAuthn', err);
      case 'SecurityError':
        return new WebAuthnError('NOT_ALLOWED', 'Security error: check RP ID and origin', err);
      default:
        return new WebAuthnError(
          'UNKNOWN',
          `WebAuthn DOMException: ${err.name} — ${err.message}`,
          err,
        );
    }
  }
  return new WebAuthnError('UNKNOWN', `Unexpected error: ${String(err)}`, err);
}

/** Generate cryptographically random bytes. */
function randomBytes(length = 32): Uint8Array<ArrayBuffer> {
  const buf = new ArrayBuffer(length);
  const bytes = new Uint8Array(buf);
  crypto.getRandomValues(bytes);
  return bytes;
}

// ---------------------------------------------------------------------------
// Registration
// ---------------------------------------------------------------------------

export interface RegisterAttesterOptions {
  /** Must match the current origin's effective domain. */
  rpId: string;
  rpName: string;
  /** Display name shown in the passkey prompt. */
  label: string;
}

/**
 * Register a new attester via a WebAuthn passkey (navigator.credentials.create).
 *
 * Flow:
 *   1. POST /v1/attesters/register/options to get server challenge.
 *   2. Create an ES256 passkey on the device.
 *   3. Extract the credentialId and SPKI public key.
 *   4. POST to /v1/attesters/register to store the credential server-side.
 *   5. Return the AttesterIdentity as stored.
 *
 * Throws WebAuthnError on ceremony failure.
 */
export async function registerAttester(
  options: RegisterAttesterOptions,
): Promise<AttesterIdentity> {
  const { rpId, rpName, label } = options;

  // Get registration options from server (includes server challenge as base64url string).
  const creationOptions = await apiFetch<
    PublicKeyCredentialCreationOptions & { challenge?: string | ArrayBuffer }
  >('/v1/attesters/register/options', { method: 'POST', body: { label } });

  // The server challenge may be a base64url string (from JSON) or already an ArrayBuffer.
  // Decode to raw bytes before passing to credentials.create.
  // A missing challenge is a broken server contract and must fail loudly —
  // substituting local randomness would silently produce a registration the
  // server cannot bind to its issued challenge.
  let challengeBytes: Uint8Array<ArrayBuffer>;
  const rawChallenge = creationOptions.challenge;
  if (typeof rawChallenge === 'string') {
    // base64url string from the JSON API response.
    challengeBytes = decodeBase64url(rawChallenge);
  } else if (rawChallenge instanceof ArrayBuffer) {
    const buf = new ArrayBuffer(rawChallenge.byteLength);
    new Uint8Array(buf).set(new Uint8Array(rawChallenge));
    challengeBytes = new Uint8Array(buf);
  } else {
    throw new WebAuthnError(
      'UNKNOWN',
      'Server did not provide a registration challenge (register/options response is malformed)',
    );
  }

  // User handle: stable opaque ID. Use a random value since we don't have
  // a server-assigned user id at registration time in this flow.
  const userHandle = randomBytes(16);

  let credential: PublicKeyCredential;
  try {
    const raw = await navigator.credentials.create({
      publicKey: {
        challenge: challengeBytes,
        rp: { id: rpId, name: rpName },
        user: {
          id: userHandle,
          name: label,
          displayName: label,
        },
        pubKeyCredParams: [{ type: 'public-key', alg: -7 }],
        authenticatorSelection: {
          authenticatorAttachment: 'platform',
          requireResidentKey: true,
          residentKey: 'required',
          userVerification: 'required',
        },
        attestation: 'none',
        timeout: 60000,
      },
    });
    if (raw === null || typeof raw !== 'object' || (raw as Credential).type !== 'public-key') {
      throw new WebAuthnError('NO_AUTHENTICATOR', 'credentials.create returned null');
    }
    credential = raw as PublicKeyCredential;
  } catch (err) {
    if (err instanceof WebAuthnError) throw err;
    throw mapCredentialError(err);
  }

  const response = credential.response as AuthenticatorAttestationResponse;
  const spkiDer = response.getPublicKey();
  if (spkiDer === null) {
    throw new WebAuthnError(
      'UNKNOWN',
      'AuthenticatorAttestationResponse.getPublicKey() returned null',
    );
  }

  // credId: base64url-encoded credential id.
  const credId = encodeBase64url(new Uint8Array(credential.rawId));
  // spkiPublicKey: base64url-encoded SPKI DER. gg-app decodes base64url and
  // stores/serves the key as standard base64 in bundles (CONTRACT.md). The
  // field was previously misnamed `cosePublicKey`; gg-app keeps that as a
  // serde alias, so this rename needs no coordinated deploy.
  const spkiPublicKey = encodeBase64url(new Uint8Array(spkiDer));

  const identity = await apiFetch<AttesterIdentity>('/v1/attesters/register', {
    method: 'POST',
    body: { credId, spkiPublicKey, label },
  });

  return identity;
}

// ---------------------------------------------------------------------------
// Attestation
// ---------------------------------------------------------------------------

/**
 * Options returned by POST /vehicles/:id/attestations/options.
 *
 * The server stamps `createdAt` at options-request time in canonical Z form.
 * The client MUST echo this exact string into the attestation — never generate
 * its own. The challenge is already computed server-side; callers may use the
 * returned `challenge` bytes directly (skipping local re-derivation if preferred).
 */
export interface AttestationOptions {
  vehicleId: string;
  headHash: string;
  statement: string;
  /** Server-stamped createdAt (RFC 3339 Z form, e.g. "2026-05-25T12:11:00Z"). Echo verbatim. */
  createdAt: string;
  /** SHA-256 of canonicalJSON(attestation event), as lowercase hex. */
  challengeHex: string;
  /** Same digest as raw bytes, base64url. Pass to WebAuthn challenge. */
  challenge: string;
  rpId: string;
}

export interface AttestOptions {
  /** RP ID — use the server-returned rpId from AttestationOptions. */
  rpId: string;
  /** The attester's stored credential id (base64url, matches AttesterIdentity.credId). */
  credId: string;
  /** The vehicle being attested. */
  vehicleId: string;
  /** The current ledger head hash. */
  headHash: string;
  /** The expert's statement to bind. */
  statement: string;
  /**
   * Server-stamped createdAt (RFC 3339 Z form).
   *
   * MUST be the exact value returned by POST …/attestations/options.
   * The challenge is hashed over this exact string — do not regenerate it.
   * This ensures the browser's challenge computation matches the server's.
   */
  createdAt: string;
}

export interface AttestationComponents {
  authenticatorData: string;
  clientDataJSON: string;
  /** DER ECDSA signature, base64url (as emitted by the authenticator). */
  signature: string;
}

/**
 * Perform a WebAuthn assertion ceremony to attest to a vehicle's ledger head.
 *
 * The `createdAt` in AttestOptions MUST be the server-stamped value from
 * POST …/attestations/options — never regenerated by the client. The challenge
 * is derived locally from the exact `createdAt` string so it matches what the
 * server computed when it issued the options. Passing a different `createdAt`
 * causes challenge mismatch and the attestation will be rejected (422).
 *
 * Challenge = SHA-256(canonicalJSON({vehicleId, headHash, statement, createdAt}))
 * as raw 32 bytes (base64url in clientDataJSON).
 *
 * Returns the raw assertion components for POST /vehicles/:id/attestations.
 * Throws WebAuthnError on ceremony failure.
 */
export async function attestLedgerHead(options: AttestOptions): Promise<AttestationComponents> {
  const { rpId, credId, vehicleId, headHash, statement, createdAt } = options;

  // Compute challenge = sha256(canonicalJSON(event)) → raw 32 bytes as base64url.
  const challengeB64url = await computeAttestationChallenge({
    vehicleId,
    headHash,
    statement,
    createdAt,
  });

  // Decode base64url → raw 32 bytes for the WebAuthn challenge ArrayBuffer.
  const challengeBytes = decodeBase64url(challengeB64url);
  const credIdBytes = decodeBase64url(credId);

  let assertion: PublicKeyCredential;
  try {
    const raw = await navigator.credentials.get({
      publicKey: {
        challenge: challengeBytes,
        rpId,
        allowCredentials: [{ type: 'public-key', id: credIdBytes }],
        userVerification: 'required',
        timeout: 60000,
      },
    });
    if (raw === null || typeof raw !== 'object' || (raw as Credential).type !== 'public-key') {
      throw new WebAuthnError('NO_AUTHENTICATOR', 'credentials.get returned null');
    }
    assertion = raw as PublicKeyCredential;
  } catch (err) {
    if (err instanceof WebAuthnError) throw err;
    throw mapCredentialError(err);
  }

  const response = assertion.response as AuthenticatorAssertionResponse;

  return {
    authenticatorData: encodeBase64url(new Uint8Array(response.authenticatorData)),
    clientDataJSON: encodeBase64url(new Uint8Array(response.clientDataJSON)),
    // signature is DER-encoded as emitted by the authenticator; kept as-is.
    signature: encodeBase64url(new Uint8Array(response.signature)),
  };
}

// ---------------------------------------------------------------------------
// Availability check
// ---------------------------------------------------------------------------

/**
 * Returns true if the browser and platform support WebAuthn with user verification.
 */
export async function isWebAuthnAvailable(): Promise<boolean> {
  if (typeof window === 'undefined' || !window.PublicKeyCredential) {
    return false;
  }
  const pkc = window.PublicKeyCredential;
  if (typeof pkc.isUserVerifyingPlatformAuthenticatorAvailable !== 'function') {
    return false;
  }
  try {
    return await pkc.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch {
    return false;
  }
}
